
import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";

const API_BASE = "/api/dealdesk";
const ROUTES = {
  workspace: "/workspace",
  configure: "/quotes/configure",
  submit: "/quotes/submit",
  comment: "/approvals/comment",
  decision: "/approvals/decision",
  proposal: "/quotes/proposal?quoteId=quote-dd-1048",
  reset: "/demo/reset"
};

const money = value => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(value || 0));
const stamp = value => value ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(value)) : "—";
const titleCase = value => String(value || "").replaceAll("_", " ");

async function request(path, options = {}) {
  const response = await fetch(API_BASE + path, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) }
  });
  const type = response.headers.get("content-type") || "";
  if (!type.includes("application/json")) throw new Error(options.method + " " + API_BASE + path + " returned " + type + " instead of JSON");
  const data = await response.json();
  if (!response.ok || data.ok === false) throw new Error(data.error || ("Request failed with " + response.status));
  return data;
}

function Status({ value }) {
  return <span className={"status status-" + value}>{titleCase(value)}</span>;
}

function Metric({ icon, label, value, detail, positive }) {
  return <article className="metric">
    <div className="metric-top"><span>{label}</span><span className="metric-icon">{icon}</span></div>
    <strong>{value}</strong><small className={positive ? "up" : ""}>{detail}</small>
  </article>;
}

function PageHead({ eyebrow, title, subtitle, children }) {
  return <header className="page-head">
    <div><div className="eyebrow">{eyebrow}</div><h1>{title}</h1><p>{subtitle}</p></div>
    {children && <div className="button-row">{children}</div>}
  </header>;
}

function Dashboard({ data, go }) {
  const quote = data.quote;
  const account = data.accounts.find(item => item.id === quote.accountId);
  return <>
    <PageHead eyebrow="Revenue command center" title="Good morning, Maya." subtitle="Here’s what needs attention across your active deal desk.">
      <button className="btn btn-secondary" onClick={() => go("proposal")}>Proposal preview</button>
      <button className="btn btn-primary" onClick={() => go("quote")}>＋ New quote</button>
    </PageHead>
    <section className="metric-grid">
      <Metric icon="↗" label="Pipeline value" value={money(data.summary.pipelineValue)} detail="↑ 12.4% this quarter" positive />
      <Metric icon="▤" label="Open quotes" value={data.summary.openQuotes} detail="1 requires attention" />
      <Metric icon="✓" label="Pending approvals" value={data.summary.pendingApprovals} detail={data.summary.pendingApprovals ? "Action needed today" : "Queue is clear"} positive={!data.summary.pendingApprovals} />
      <Metric icon="◒" label="Projected margin" value={data.economics.grossMarginPct + "%"} detail={"Policy floor 35%"} positive={data.economics.grossMarginPct >= 35} />
    </section>
    <section className="dashboard-grid">
      <article className="panel">
        <div className="panel-head"><div><h2>Active quotes</h2><p>Live economics and approval status</p></div><button className="btn btn-secondary" onClick={() => go("quote")}>View workspace</button></div>
        <div className="table-wrap"><table className="data-table"><thead><tr><th>Account</th><th>Quote</th><th>Net price</th><th>Margin</th><th>Status</th><th>Owner</th></tr></thead><tbody>
          <tr onClick={() => go("quote")}>
            <td><div className="entity"><span className="entity-mark">N</span><span><strong>{account?.name}</strong><small>{account?.segment}</small></span></div></td>
            <td><strong>{quote.number}</strong><div className="product-sku">{quote.title}</div></td>
            <td><strong>{money(data.economics.netPrice)}</strong></td>
            <td>{data.economics.grossMarginPct}%</td><td><Status value={quote.status} /></td>
            <td><div className="entity"><span className="comment-avatar">MC</span><span>Maya Chen</span></div></td>
          </tr>
        </tbody></table></div>
      </article>
      <aside className="panel">
        <div className="panel-head"><div><h2>Policy radar</h2><p>Server-evaluated exception signals</p></div><span className={"risk-tag " + data.policy.risk}>{data.policy.risk} risk</span></div>
        <div className="panel-body">
          <div className="approval-card">
            <div className="risk-line"><span className="approval-title">{quote.number} · {account?.name}</span><Status value={quote.status} /></div>
            <div className="approval-meta"><span>{quote.discountPct}% discount</span><span>{data.economics.grossMarginPct}% margin</span></div>
          </div>
          <div className="rule-list">
            {data.discountPolicies.map(rule => <div className="rule" key={rule.id}><b>{rule.stage === "finance" ? "F" : "M"}</b><span><strong>{rule.name}</strong><br />{rule.metric === "discountPct" ? "Discount" : "Gross margin"} {rule.operator} {rule.threshold}%</span></div>)}
          </div>
        </div>
      </aside>
    </section>
  </>;
}

function QuoteStudio({ data, discount, setDiscount, busy, configure, submit }) {
  const e = data.economics;
  const policy = data.policy;
  return <>
    <PageHead eyebrow="Quote workspace" title={data.quote.title} subtitle={"Opportunity · " + data.opportunities[0]?.name}>
      <button className="btn btn-secondary">Save draft</button>
      <button className="btn btn-primary" disabled={busy} onClick={submit}>Submit for approval →</button>
    </PageHead>
    <section className="studio-grid">
      <article className="panel">
        <div className="quote-banner"><div><span className="quote-number">{data.quote.number} · VERSION {data.quote.version}</span><h2>{data.accounts[0]?.name}</h2><p>Valid through {data.quote.validUntil} · USD</p></div><Status value={data.quote.status} /></div>
        <div className="line-editor">
          <div className="line-row header"><span>Product</span><span>Qty</span><span>Unit price</span><span>Total</span></div>
          {data.quoteLines.map(line => <div className="line-row" key={line.id}>
            <span><div className="product-name">{line.name}</div><div className="product-sku">{line.sku}</div></span>
            <span className="qty-box">{line.quantity}</span><span>{money(line.unitPrice)}</span><strong>{money(line.quantity * line.unitPrice)}</strong>
          </div>)}
        </div>
        <div className="discount-control">
          <div className="control-label"><span>Customer discount</span><span className="discount-value">{discount}%</span></div>
          <input aria-label="Customer discount" type="range" min="0" max="35" value={discount} onChange={event => setDiscount(Number(event.target.value))} />
          <div className="range-labels"><span>0% · Standard</span><span>15% · Manager</span><span>25% · Finance</span><span>35%</span></div>
          <div className="button-row" style={{marginTop:14}}><button className="btn btn-secondary" onClick={() => setDiscount(12)}>Standard · 12%</button><button className="btn btn-secondary" onClick={() => setDiscount(18)}>Growth · 18%</button><button className="btn btn-secondary" onClick={() => setDiscount(28)}>Strategic · 28%</button></div>
          <div className="button-row" style={{marginTop:9}}><button className="btn btn-primary" disabled={busy} onClick={configure}>Recalculate policy</button></div>
        </div>
      </article>
      <aside className="panel econ-card">
        <div className="econ-title"><h3>Deal economics</h3><span className={"risk-tag " + policy.risk}>{policy.risk} risk</span></div>
        <div className="econ-rows">
          <div className="econ-row"><span>List subtotal</span><strong>{money(e.subtotal)}</strong></div>
          <div className="econ-row"><span>Discount ({e.discountPct}%)</span><strong>−{money(e.discountAmount)}</strong></div>
          <div className="econ-row total"><span>Net price</span><strong>{money(e.netPrice)}</strong></div>
          <div className="econ-row"><span>Estimated cost</span><strong>{money(e.estimatedCost)}</strong></div>
          <div className="econ-row"><span>Gross profit</span><strong>{money(e.grossProfit)}</strong></div>
        </div>
        <div className="margin-meter"><div className="margin-head"><span>Projected gross margin</span><strong>{e.grossMarginPct}%</strong></div><div className="bar"><span style={{width: Math.min(100,e.grossMarginPct) + "%"}} /></div><div className="threshold">Policy floor · 35%</div></div>
        <div className={"policy-box " + (!policy.requiresApproval ? "safe" : "")}>
          <strong>{policy.requiresApproval ? "⚡ Approval required" : "✓ Within policy"}</strong>
          <p>{policy.reasons[0] || "This quote can proceed without an exception."}</p>
          {policy.reasons.slice(1).map(reason => <p key={reason}>{reason}</p>)}
        </div>
      </aside>
    </section>
  </>;
}

function Approvals({ data, comment, setComment, addComment, decide, busy }) {
  const approval = data.approvalRequest;
  const completed = approval?.completedStages || [];
  const current = approval?.currentStage;
  return <>
    <PageHead eyebrow="Approval queue" title="Decision workspace" subtitle="Review economics, discuss the exception, and leave an auditable decision.">
      <button className="btn btn-secondary">Policy guide</button>
    </PageHead>
    <section className="approval-layout">
      <article className="panel">
        <div className="panel-head"><div><h2>{data.quote.number} · {data.accounts[0]?.name}</h2><p>{data.quote.discountPct}% discount · {data.economics.grossMarginPct}% projected margin</p></div><Status value={data.quote.status} /></div>
        <div className="panel-body">
          <div className="timeline">
            <div className="step done"><span className="step-dot">✓</span><div className="step-copy"><strong>Sales submitted</strong><p>Maya Chen · Proposal economics locked for review</p></div></div>
            <div className={"step " + (completed.includes("manager") ? "done" : current === "manager" ? "current" : "")}><span className="step-dot">{completed.includes("manager") ? "✓" : "M"}</span><div className="step-copy"><strong>Manager review</strong><p>{completed.includes("manager") ? "Approved by Jordan Lee" : "Discount and margin exception review"}</p></div></div>
            <div className={"step " + (completed.includes("finance") ? "done" : current === "finance" ? "current" : "")}><span className="step-dot">{completed.includes("finance") ? "✓" : "F"}</span><div className="step-copy"><strong>Finance review</strong><p>{completed.includes("finance") ? "Approved by Priya Shah" : data.policy.requiredStages.includes("finance") ? "Required above 25% discount" : "Not required for this quote"}</p></div></div>
          </div>
          <div className="button-row">
            {current === "manager" && <><button className="btn btn-success" disabled={busy} onClick={() => decide("manager","approve")}>✓ Approve manager stage</button><button className="btn btn-danger" disabled={busy} onClick={() => decide("manager","reject")}>Reject</button></>}
            {current === "finance" && <><button className="btn btn-success" disabled={busy} onClick={() => decide("finance","approve")}>✓ Approve finance stage</button><button className="btn btn-danger" disabled={busy} onClick={() => decide("finance","reject")}>Reject</button></>}
            {data.quote.status === "approved" && <span className="status status-approved">Decision complete</span>}
            {data.quote.status === "draft" && <span className="status status-draft">Submit the quote to begin</span>}
          </div>
        </div>
      </article>
      <aside className="panel">
        <div className="panel-head"><div><h2>Decision room</h2><p>Discussion is stored in the audit trail</p></div><span>{data.comments.length}</span></div>
        <div className="panel-body">
          {data.comments.length ? data.comments.map(item => <div className="comment" key={item.id}><span className="comment-avatar">{item.actorName.split(" ").map(x=>x[0]).join("")}</span><div><strong>{item.actorName}</strong><time>{stamp(item.createdAt)}</time><p>{item.message}</p></div></div>) : <div className="empty">No comments yet. Add the first review note.</div>}
          <div className="comment-form"><input value={comment} onChange={event => setComment(event.target.value)} placeholder="Add a decision note…" /><button className="btn btn-primary" disabled={busy || comment.trim().length < 4} onClick={addComment}>Send</button></div>
        </div>
      </aside>
    </section>
  </>;
}

function Proposal({ data }) {
  return <>
    <PageHead eyebrow="Proposal preview" title="Customer-ready commercial summary" subtitle="Economics and decision state are loaded from the persisted quote.">
      <button className="btn btn-secondary">Download PDF</button><button className="btn btn-primary">Share proposal</button>
    </PageHead>
    <article className="proposal">
      <div className="proposal-top"><div><span className="quote-number">PROPOSAL · {data.quote.number}</span><h2>{data.quote.title}</h2><p>Prepared by Maya Chen · DealDesk Revenue Team</p></div><Status value={data.quote.status} /></div>
      <div className="proposal-body">
        <div className="proposal-parties"><div><span className="label">Prepared for</span><strong>{data.accounts[0]?.name}</strong><p>{data.accounts[0]?.industry} · {data.accounts[0]?.region}</p></div><div><span className="label">Valid through</span><strong>{data.quote.validUntil}</strong><p>Annual billing · USD</p></div></div>
        <div className="table-wrap"><table className="data-table"><thead><tr><th>Solution</th><th>Quantity</th><th>Unit price</th><th>Investment</th></tr></thead><tbody>{data.quoteLines.map(line=><tr key={line.id}><td><strong>{line.name}</strong><div className="product-sku">{line.sku}</div></td><td>{line.quantity}</td><td>{money(line.unitPrice)}</td><td>{money(line.quantity*line.unitPrice)}</td></tr>)}</tbody></table></div>
        <div className="total-box"><span><span className="label">Net annual investment</span><p>{data.quote.discountPct}% commercial adjustment applied</p></span><strong>{money(data.economics.netPrice)}</strong></div>
      </div>
    </article>
    <article className="panel" style={{maxWidth:900,margin:"18px auto 0"}}>
      <div className="panel-head"><div><h2>Persisted audit proof</h2><p>Every policy evaluation, comment, and decision</p></div><span className="status status-approved">{data.auditEvents.length} events</span></div>
      <div className="panel-body audit-list">{data.auditEvents.slice(0,7).map(event=><div className="audit-item" key={event.id}><span className="audit-dot"/><span><strong>{event.message}</strong><p>{titleCase(event.type)}</p></span><time>{stamp(event.createdAt)}</time></div>)}</div>
    </article>
  </>;
}

function App() {
  const [tab,setTab]=useState("dashboard");
  const [data,setData]=useState(null);
  const [discount,setDiscount]=useState(12);
  const [comment,setComment]=useState("");
  const [busy,setBusy]=useState(false);
  const [notice,setNotice]=useState(null);
  const [error,setError]=useState(null);

  const load = async () => {
    try {
      const next = await request(ROUTES.workspace, { method: "GET" });
      setData(next); setDiscount(next.quote.discountPct); setError(null);
    } catch (err) { setError(err.message); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!notice) return; const timer=setTimeout(()=>setNotice(null),3500); return()=>clearTimeout(timer); }, [notice]);

  const act = async (label, path, body, nextTab) => {
    setBusy(true);
    try { await request(path,{method:"POST",body:JSON.stringify(body||{})}); await load(); setNotice(label); if(nextTab)setTab(nextTab); }
    catch(err){setNotice({error:err.message});}
    finally{setBusy(false);}
  };
  const configure=()=>act("Policy recalculated from persisted quote economics",ROUTES.configure,{quoteId:"quote-dd-1048",discountPct:discount},"quote");
  const submit=()=>act("Quote submitted into the approval workflow",ROUTES.submit,{quoteId:"quote-dd-1048"},"approvals");
  const addComment=()=>act("Comment added to the audit trail",ROUTES.comment,{quoteId:"quote-dd-1048",actorId:"usr-jordan",message:comment},"approvals").then(()=>setComment(""));
  const decide=(stage,decision)=>act((stage==="finance"?"Finance":"Manager")+" decision persisted",ROUTES.decision,{quoteId:"quote-dd-1048",stage,decision},"approvals");
  const reset=()=>act("Demo restored to the 12% draft baseline",ROUTES.reset,{},"dashboard");

  const tabs=useMemo(()=>[
    ["dashboard","⌂","Overview"],["quote","◇","Quote studio"],["approvals","✓","Approvals"],["proposal","▤","Proposal"]
  ],[]);

  if(error) return <div className="loading"><div><h2>DealDesk could not load</h2><p>{error}</p><button className="btn btn-primary" onClick={load}>Retry live API</button></div></div>;
  if(!data) return <div className="loading"><div><div className="spinner"/><p>Loading persisted deal economics…</p></div></div>;

  return <div className="app-shell">
    <aside className="sidebar">
      <div className="brand"><span className="brand-mark">D</span><span>DealDesk</span></div>
      <div className="workspace-label">Workspace</div>
      <nav className="nav-list">{tabs.map(([id,icon,label])=><button className={"nav-item "+(tab===id?"active":"")} key={id} onClick={()=>setTab(id)}><span className="nav-icon">{icon}</span><span>{label}</span></button>)}</nav>
      <div className="workspace-label" style={{marginTop:24}}>Tools</div>
      <nav className="nav-list"><button className="nav-item"><span className="nav-icon">⌕</span><span>Accounts</span></button><button className="nav-item"><span className="nav-icon">⚙</span><span>Policy center</span></button></nav>
      <div className="sidebar-card"><strong>OBTO persistence</strong><p>Live API data · no browser fallback</p><div className="mini-row"><span className="pulse"/> All systems operational</div></div>
    </aside>
    <main className="main">
      <div className="topbar"><div className="breadcrumbs"><span>Revenue operations</span><span>›</span><strong>{tabs.find(x=>x[0]===tab)?.[2]}</strong></div><div className="top-actions"><div className="search">⌕<input placeholder="Search quotes, accounts…" /></div><button className="btn btn-secondary" disabled={busy} onClick={reset}>↻ Reset demo</button><span className="avatar">MC</span></div></div>
      <div className="content">
        {tab==="dashboard"&&<Dashboard data={data} go={setTab}/>}
        {tab==="quote"&&<QuoteStudio data={data} discount={discount} setDiscount={setDiscount} busy={busy} configure={configure} submit={submit}/>}
        {tab==="approvals"&&<Approvals data={data} comment={comment} setComment={setComment} addComment={addComment} decide={decide} busy={busy}/>}
        {tab==="proposal"&&<Proposal data={data}/>}
      </div>
    </main>
    <nav className="mobile-tabs">{tabs.map(([id,icon,label])=><button className={"mobile-tab "+(tab===id?"active":"")} key={id} onClick={()=>setTab(id)}><b>{icon}</b>{label}</button>)}</nav>
    {notice&&<div className={"toast "+(notice.error?"error":"")}>{notice.error?"API error: "+notice.error:"✓ "+notice}</div>}
  </div>;
}

const mount = () => {
  const element = document.getElementById("root");
  if (element) createRoot(element).render(<App />);
};
document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", mount, { once: true }) : mount();
